import React from "react";

type BadgeVariant = "solid" | "outline";

/**
 * Props for the `Badge` component.
 *
 * @property children - The content to be displayed inside the badge.
 * @property color - Optional. The color of the badge, matched against SCSS utility classes.
 * @property variant - Optional. The visual variant of the badge.
 * @property className - Optional. Additional CSS class names to apply to the badge.
 */
interface BadgeProps {
  children: React.ReactNode;
  color?: string; // free-form, matched against SCSS utilities
  variant?: BadgeVariant;
  className?: string;
}

/**
 * Renders a customizable badge component for displaying short, highlighted text.
 *
 * @param children - The content to be displayed inside the badge.
 * @param color - The color theme of the badge (default: "navy").
 * @param variant - The visual style of the badge, either "solid" or "outline" (default: "solid").
 * @param className - Additional CSS classes to apply to the badge.
 *
 * @example
 * ```tsx
 * <Badge color="green" variant="outline">Active</Badge>
 * ```
 */
const Badge: React.FC<BadgeProps> = ({
  children,
  color = "navy",
  variant = "solid",
  className = "",
}) => {
  const classes = [
    "px-10px py-3px radius-5px fs-14px fw-bold",
    variant === "solid" ? `bg-${color}` : `bg-transparent border-${color}`,
    variant === "outline" ? `text-${color}` : "text-white",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return <span className={classes}>{children}</span>;
};

export default Badge;

// usage <Badge color="navy" variant="outline">Outline</Badge>
